/* import __COLOCATED_TEMPLATE__ from './conversation-details-sidebar.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type InboxState from 'embercom/services/inbox-state';
import Conversation, { NewConversation } from 'embercom/objects/inbox/conversation';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import type TeamSummary from 'embercom/objects/inbox/team-summary';
import type User from 'embercom/objects/inbox/user';

import { trackedReset } from 'tracked-toolbox';
import { type SupportedSidebarSection } from 'embercom/services/inbox-sidebar-service';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import type Session from 'embercom/services/session';
import type CopilotApi from 'embercom/services/copilot-api';
import type LinkedTicketsResource from './linked-tickets-resource';
import type ConversationActiveParticipant from 'embercom/services/conversation-active-participant';

export interface ConversationDetailsSidebarArgs {
  conversation: Conversation | NewConversation;
  linkedTicketsResource: LinkedTicketsResource;
  openSidebarSections?: Array<string | number>;
  onOpenSidebarSectionsChange?: Function;
  onAdminUpdate(assignee: AdminWithPermissions): void;
  onTeamUpdate(team: TeamSummary): void;
  onUserUpdated?: (user: User) => void;
  sidebarSections?: SupportedSidebarSection[];
  onCollapse: () => unknown;
  setTicketTypeToAddToConversation: (ticketTypeId: number) => void;
  setTicketTypeToChangeType?: (ticketTypeId: number) => void;
  isResizable?: boolean;
  canOpenInNewTab?: boolean;
  isFullConversationPanel?: boolean;
  isModule?: boolean;
  toggleVisibility?: () => unknown;
  duplicateData?: { users: User[]; moreExist: boolean };
}

interface Signature {
  Args: ConversationDetailsSidebarArgs;
}

export default class ConversationDetailsSidebar extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare session: Session;
  @service declare copilotApi: CopilotApi;
  @service declare conversationActiveParticipant: ConversationActiveParticipant;

  @tracked lastConversationId?: number;

  /**
   * Memo key that triggers participant reset when:
   * - The conversation changes (switching between conversations)
   * - The first participant changes (different user becomes first)
   * - The participant count changes (participant added or removed)
   * This ensures the sidebar updates immediately when conversations switch
   * or when participants are modified within a conversation.
   */
  get participantMemoKey(): string {
    let conversationId = this.args.conversation.id ?? 'none';
    let firstParticipantId = this.args.conversation.firstParticipant?.id ?? 'none';
    let participantCount = this.args.conversation.participantSummaries?.length ?? 0;
    return `${conversationId}-${firstParticipantId}-${participantCount}`;
  }

  @trackedReset({
    memo: 'participantMemoKey',
    update() {
      return this.getActiveParticipant();
    },
  })
  userId: string | undefined = undefined;
  readonly defaultInbox = { id: 'all', category: 'shared' };

  constructor(owner: unknown, args: ConversationDetailsSidebarArgs) {
    super(owner, args);

    if (this.args.conversation instanceof Conversation) {
      this.lastConversationId = this.args.conversation.id;
    }
  }

  get inbox() {
    return this.inboxState.activeInbox || this.defaultInbox;
  }

  get showPreviewPanel() {
    return this.inboxSidebarService.isPreviewingConversation;
  }

  get shouldShowEvenlySplitPanels() {
    return this.args.isFullConversationPanel && this.isPreviewPanelOpen;
  }

  get isPreviewPanelOpen() {
    return (
      this.inboxSidebarService.isViewingSideConversation ||
      this.inboxSidebarService.isViewingNewSideConversationPanel ||
      this.inboxState.isConversationListHidden ||
      this.inboxSidebarService.isPreviewingConversation ||
      this.inboxSidebarService.isViewingStartLinkedConversation ||
      this.inboxSidebarService.isViewingKnowledgeBase ||
      this.inboxSidebarService.isViewingCopilotSourcePreview ||
      this.inboxSidebarService.isPreviewingLinkedItem
    );
  }

  get shouldShowSidebarShadow() {
    return !this.args.isFullConversationPanel && this.isPreviewPanelOpen;
  }

  get copilotAnalyticsData() {
    if (!(this.args.conversation instanceof Conversation)) {
      return {};
    }

    let lastRenderablePart = this.args.conversation.lastRenderablePart;
    let lastTeammatePart =
      this.args.conversation.humanAdminComments?.sortBy('createdAt').lastObject;
    let lastEndUserPart = this.args.conversation.userComments?.sortBy('createdAt').lastObject;

    return {
      conversation_id: this.args.conversation.id,
      last_part_entity_type: lastRenderablePart?.entityType,
      last_part_entity_id: lastRenderablePart?.entityId,
      last_teammate_part_entity_id: lastTeammatePart?.entityId,
      last_teammate_part_entity_type: lastTeammatePart?.entityType,
      last_end_user_part_entity_id: lastEndUserPart?.entityId,
      last_end_user_part_entity_type: lastEndUserPart?.entityType,
    };
  }

  get shouldRenderOverflowYScroll(): boolean {
    if (this.inboxSidebarService.activeTab === 'copilot' && this.copilotApi.isCopilotEnabled) {
      return true;
    } else if (this.inboxSidebarService.activeTab === 'details') {
      return true;
    } else if (this.inboxSidebarService.activeTab === 'score') {
      return false;
    } else {
      return false;
    }
  }

  @action onConversationChange() {
    let { conversation } = this.args;

    if (conversation instanceof Conversation && conversation.id !== this.lastConversationId) {
      this.inboxSidebarService.isPreviewingConversation = false;
      this.inboxSidebarService.toggleIsViewingStartLinkedConversation({ visibility: false });
      this.inboxSidebarService.closeNewSideConversationPanel();
      this.inboxSidebarService.isViewingPhoneDetails = false;
      this.lastConversationId = conversation.id;
    }
  }

  @action onPreviewConversationChange(isPreviewing = false) {
    this.inboxSidebarService.isPreviewingConversation = isPreviewing;
  }

  @action onTeamUpdate(team: TeamSummary) {
    if (this.args.conversation instanceof NewConversation) {
      this.args.onTeamUpdate(team);
    } else {
      this.inboxState.assignConversationToTeam(this.args.conversation, team);
    }
  }

  @action onAdminUpdate(admin: AdminWithPermissions) {
    if (this.args.conversation instanceof NewConversation) {
      this.args.onAdminUpdate(admin);
    } else {
      this.inboxState.assignConversationToAdmin(this.args.conversation, admin);
    }
  }

  @action async onSelectTicketType(ticketTypeId: number) {
    if (this.args.conversation instanceof NewConversation) {
      return;
    }

    this.args.setTicketTypeToAddToConversation(ticketTypeId);
  }

  @action onExpandedSectionChange(sectionId: SupportedSidebarSection | number | undefined) {
    this.inboxSidebarService.onExpandedSectionChange(
      sectionId,
      this.args.conversation instanceof Conversation ? this.args.conversation?.id : undefined,
    );
  }

  @action openKnowledgePanelSheet() {
    this.inboxSidebarService.openKnowledgeBasePanel(this.args.conversation.id, this.userId);
  }

  getActiveParticipant() {
    let conversationId = String(this.args.conversation.id);
    let savedParticipantId =
      this.conversationActiveParticipant.getActiveParticipantId(conversationId);

    // Validate saved participant still exists in the conversation
    // This handles the case where a participant was removed but their ID was still cached
    if (savedParticipantId) {
      let participantStillExists = this.args.conversation.participantSummaries?.some(
        (participant) => String(participant.id) === savedParticipantId,
      );

      if (participantStillExists) {
        return savedParticipantId;
      }

      // Clear the stale participant ID since they're no longer in the conversation
      this.conversationActiveParticipant.clearActiveParticipantId(conversationId);
    }

    // Use first participant ID if available
    if (this.args.conversation.firstParticipant?.id) {
      return String(this.args.conversation.firstParticipant.id);
    }

    return undefined;
  }

  @action updateActiveParticipant(participantId: string) {
    this.userId = participantId;

    if (!this.args.conversation?.id) {
      return;
    }

    this.conversationActiveParticipant.setActiveParticipantId(
      String(this.args.conversation.id),
      participantId,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar': typeof ConversationDetailsSidebar;
    'inbox2/conversation-details-sidebar': typeof ConversationDetailsSidebar;
  }
}
